/**
 * Merchant Pro V1 PR-4b — Notification Bell button (topbar mount).
 *
 * - Polls /api/owner/notifications?limit=1 every 15s to refresh unread badge.
 * - Click → open NotificationSlideOver (rendered by parent layout).
 * - Hides badge when unread_count === 0.
 *
 * Operator-locked: polling cadence is 15s for the bell. No SSE/WS.
 */
import { useQuery } from '@tanstack/react-query';
import { Bell } from 'lucide-react';
import { api } from '@/lib/api';
import { qk } from '@/lib/queryKeys';
import { useAuthStore } from '@/lib/auth';

export default function NotificationBellButton({ onOpen }) {
  const { token } = useAuthStore();
  const { data } = useQuery({
    queryKey: qk.ownerNotificationsUnreadCount(),
    queryFn: () => api.ownerNotifications(token, { limit: 1 }),
    enabled: !!token,
    refetchInterval: 15_000,
    refetchOnWindowFocus: true,
  });
  const unread = data?.unread_count ?? 0;
  const hasBadge = unread > 0;

  return (
    <button
      type="button"
      onClick={onOpen}
      data-testid="notif-bell-button"
      data-unread={unread}
      className="relative inline-flex items-center justify-center w-10 h-10 rounded-2xl text-white/80 hover:text-white hover:bg-white/5 transition-colors"
      aria-label="Notifications"
    >
      <Bell size={18} />
      {hasBadge ? (
        <span
          data-testid="notif-bell-badge"
          className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 rounded-full text-[10px] font-bold flex items-center justify-center"
          style={{
            background: '#D4AF37',
            color: '#0A0A0A',
            border: '2px solid #0E0E13',
          }}
        >
          {unread > 99 ? '99+' : unread}
        </span>
      ) : null}
    </button>
  );
}
